import { VillaSearchSummaryViewPropertiesType } from "@website/accommodation-ui/villa-search/villa-search-summary/villa-search-summary.types";
import { MaterialIcon } from "@website/icons/material";
import { Skeleton } from "@website/ui/skeleton";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import { useRouter } from "next/router";
import { FC, ReactNode, memo } from "react";

type PageTitleProperties = Pick<
  VillaSearchSummaryViewPropertiesType,
  "cityTitle" | "searchType" | "seoPageTitle"
> & {
  isCategoryLocation: boolean;
  fallback: ReactNode;
};

const getPageTitle = ({
  cityTitle,
  searchType,
  seoPageTitle,
  isCategoryLocation,
  fallback
}: PageTitleProperties) => {
  if (seoPageTitle) return seoPageTitle;

  if (searchType === "city") {
    const villaTitlePrefix = isCategoryLocation ? "" : "اجاره ویلا";

    return `${villaTitlePrefix} ${cityTitle}`;
  }

  return cityTitle ?? fallback;
};

const VillaSearchSummaryContent: FC<VillaSearchSummaryViewPropertiesType> = (
  properties
) => {
  const {
    seoPageTitle,
    searchType = "city",
    cityTitle,
    dateFrom,
    dateTo,
    passengersCount,
    nightCount
  } = properties;

  const router = useRouter();

  const isCompleteInfo = dateFrom && dateTo && passengersCount;
  const pageTitle = getPageTitle({
    cityTitle,
    searchType,
    seoPageTitle,
    isCategoryLocation: router.query.locationType === "category",
    fallback: <Skeleton height={20} shape="rectangle" />
  });

  const DateRange =
    dateFrom && dateTo ? (
      <span
        className="lg:text-on-surface-high-emphasis min-w-24 truncate lg:flex lg:flex-row lg:items-center lg:gap-2 lg:text-center"
        data-testid="search_date_range"
      >
        {`${dateFrom} - ${dateTo} ${
          searchType === "villa"
            ? `${numberConvert(nightCount, { locale: router.locale })} شب`
            : ""
        }`}
      </span>
    ) : (
      <span
        className={`lg:text-on-surface-high-emphasis text-secondary min-w-24 truncate lg:flex lg:flex-row lg:items-center lg:gap-2 lg:text-center ${
          searchType === "villa"
            ? "text-caption lg:text-subtitle-2 text-on-surface-high-emphasis"
            : "text-overline lg:text-subtitle-2 text-on-surface-medium-emphasis lg:text-on-surface-high-emphasis"
        }`}
        data-testid="search_date_range"
      >
        تعیین بازه اقامت
      </span>
    );

  const PassengerInfo = passengersCount ? (
    <span
      className={`lg:text-on-surface-high-emphasis truncate lg:flex lg:flex-row lg:items-center lg:gap-2 lg:text-center ${
        searchType === "villa"
          ? "text-caption lg:text-subtitle-2 text-on-surface-high-emphasis"
          : "text-overline lg:text-subtitle-2 text-on-surface-medium-emphasis lg:text-on-surface-high-emphasis"
      }`}
    >
      {`${numberConvert(passengersCount, { locale: router.locale })} نفر`}
    </span>
  ) : (
    <span className="lg:text-on-surface-high-emphasis text-secondary">
      تعداد مسافر
    </span>
  );

  return (
    <div
      className={`bg-outline-thick border-outline-thin hover:shadow-8 flex w-full items-center justify-between gap-4 rounded-full border px-4 py-px transition-shadow duration-200 ease-in-out lg:pl-px ${
        searchType === "villa"
          ? "h-[48px] bg-white lg:h-auto lg:max-w-max"
          : "bg-outline-thick lg:mx-auto lg:max-w-max"
      } ${isCompleteInfo ? "" : "border-secondary border-2"}`}
    >
      <div className="grid grow py-1.5 lg:flex lg:flex-row lg:items-center lg:gap-4">
        {(searchType === "city" || searchType === "category") && (
          <>
            <h1
              className="lg:text-on-surface-high-emphasis text-body-2 text-on-surface-high-emphasis lg:text-subtitle-2 truncate lg:flex lg:flex-row lg:items-center lg:gap-2 lg:text-center"
              data-testid="page_title"
            >
              {pageTitle}
            </h1>
            <div className="bg-outline-thin hidden h-[24px] w-px lg:block" />
          </>
        )}
        <div
          className={`lg:text-on-surface-high-emphasis truncate lg:flex lg:flex-row lg:items-center lg:gap-2 lg:text-center ${
            searchType === "villa"
              ? "text-caption lg:text-subtitle-2 text-on-surface-high-emphasis"
              : "text-overline lg:text-subtitle-2 text-on-surface-medium-emphasis lg:text-on-surface-high-emphasis"
          }`}
        >
          {DateRange}
          <div className="bg-outline-thin hidden h-[24px] w-px lg:block" />
          <span className={`${isCompleteInfo ? "" : "text-secondary"}`}>
            <span className="lg:hidden">/</span>
          </span>{" "}
          {PassengerInfo}
        </div>
      </div>
      <div
        className={`text-on-surface-medium-emphasis lg:text-on-surface-high-emphasis flex size-6 shrink-0 grow-0 items-center justify-center rounded-full text-2xl lg:size-9 ${
          isCompleteInfo
            ? "lg:text-on-color-high-emphasis text-on-surface-medium-emphasis bg-auto"
            : ""
        } ${
          isCompleteInfo && searchType === "villa"
            ? "lg:bg-secondary"
            : isCompleteInfo && searchType === "city"
              ? "lg:bg-primary"
              : ""
        } `}
      >
        <MaterialIcon
          className={`${
            isCompleteInfo
              ? "text-on-surface-medium-emphasis lg:text-white"
              : ""
          }`}
          name="search"
        />
      </div>
    </div>
  );
};

const HotelSearchSummaryContentMemoized = memo(
  VillaSearchSummaryContent,
  deepCompare
);
export { HotelSearchSummaryContentMemoized as VillaSearchSummaryContent };
